import React, { useEffect } from 'react';
import styled from 'styled-components';
import theme, { mobileBreakpoint } from '../theme';
import PageLayout from './PageLayout';
import { useNavigate } from 'react-router-dom';
import { SecondaryButton, SuccessBadgeWithIcon } from './shared/CommonElements';

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 40px 0 0 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  white-space: normal;
  word-break: break-word;
`;



const MainTitle = styled.h1`
  font-size: 64px;
  z-index: 1000;
  margin: 0;
  line-height: 1;
  color: ${props => props.theme.colors.primary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 38px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  z-index: 1000;
  font-style: italic;
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;

const ContentContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 40px 24px 48px 24px;
  background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
  color: ${theme.colors.primary};
`;

const SuccessMessage = styled.div`
  text-align: center;
  margin-bottom: 32px;
  padding: 24px;
  background: linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%);
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #4CAF50;
`;

const SuccessTitle = styled.h2`
  font-size: 1.8rem;
  color: #2e7d32;
  margin: 0 0 16px 0;
  font-weight: 600;
`;

const SuccessText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #2e7d32;
  margin: 0;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 1.3rem;
  color: ${theme.colors.primary};
  font-weight: 600;
  margin: 0 0 16px 0;
`;

const InfoBox = styled.div`
  background: #f8f9fa;
  border-radius: ${theme.borderRadius.medium};
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid #e0e0e0;
`;

const InfoText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0 0 12px 0;
  color: ${theme.colors.primary};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const HighlightText = styled.span`
  font-weight: 600;
  color: ${theme.colors.secondary};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 32px;
  
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ActionButton = styled.button`
  margin: 32px auto 0 auto;
  min-width: 300px;
  display: block;
  margin-bottom: -20px;
  padding: 14px 32px;
  background: linear-gradient(135deg, ${theme.colors.secondary} 0%, #e8316d 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: linear-gradient(135deg, #e8316d 0%, ${theme.colors.secondary} 100%);
    transform: scale(1.01);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);
  }
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: unset;
    width: 100%;
  }
`;



const ContactInfo = styled.div`
  text-align: center;
  margin-top: -10px;
  padding: 20px;
  border-radius: ${theme.borderRadius.medium};
  margin-bottom: -40px;
`;

const ContactText = styled.p`
  font-size: 1rem;
  color: ${theme.colors.primary};
  margin: 0;
`;

const ContactLink = styled.a`
  color: ${theme.colors.secondary};
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;
`;

const PurchaseSuccessPage = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageLayout>
      <HeroSection>
        <HeroTitle>
          <SuccessBadgeWithIcon />
          <MainTitle>Kauf erfolgreich!</MainTitle>
        </HeroTitle>
        <Subtitle>Dein Stundenpaket steht für dich bereit</Subtitle>
      </HeroSection>
      
      <ContentContainer>
        <SuccessMessage>
          <SuccessTitle>Vielen Dank für deinen Kauf!</SuccessTitle>
          <SuccessText>
            Dein Stundenpaket wurde erfolgreich gebucht. Du erhältst in Kürze eine Bestätigung per E-Mail.
          </SuccessText>
        </SuccessMessage>

        <Section>
          <SectionTitle>Was passiert als nächstes?</SectionTitle>
          <InfoBox>
          <InfoText>
              <HighlightText>1. Bestätigungsemail:</HighlightText> Du bekommst in Kürze eine Bestätigung per E-Mail mit allen Details zu deinem  gewählten Stundenpaket.
            </InfoText>
            <InfoText>
              <HighlightText>2. Terminvereinbarung:</HighlightText> Du kannst jetzt Termine für deine Unterrichtsstunden vereinbaren. 
              Das funktioniert ganz leicht über den <i>Neue Stunde buchen</i> Button in <i>Mein Bereich</i>.
            </InfoText>
            <InfoText>
              <HighlightText>3. Wir lernen zusammen Englisch:</HighlightText> Ich freue mich auf unsere nächste Unterrichtsstunde!
            </InfoText>
          </InfoBox>
        </Section>

        <Section>
          <SectionTitle>Wichtige Informationen</SectionTitle>
          <InfoBox>
            <InfoText>
              <HighlightText>Stundenbudget:</HighlightText> Dein gekauftes Stundenpaket steht dir als Budget zur Verfügung. 
              Du kannst es flexibel für Unterrichtsstunden des jeweiligen Stundentyps nutzen.
            </InfoText>
            <InfoText>
              <HighlightText>Gültigkeit:</HighlightText> Das Stundenbudget ist 12 Monate gültig. Nicht genutzte Stunden verfallen nach Ablauf dieser Zeit.
            </InfoText>
            <InfoText>
              <HighlightText>Zahlung:</HighlightText> Die Rechnung erhälst du mit der Bestätigungsmail. Bitte bezahle sie innerhalb der nächsten 14 Tage.
            </InfoText>
          </InfoBox>
        </Section>

        <ContactInfo>
          <ContactText>
            Hast du Fragen? Kontaktiere mich gerne unter{' '}
            <ContactLink href="mailto:mey@meyteaches.de">mey@meyteaches.de</ContactLink>
          </ContactText>
        </ContactInfo>

        <ButtonContainer>
          <ActionButton onClick={() => {
            window.scrollTo(0, 0);
            navigate('/mypage');
          }}>
            Zu meinem Bereich
          </ActionButton>
        </ButtonContainer>
      </ContentContainer>
    </PageLayout>
  );
};

export default PurchaseSuccessPage; 